import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import {
  BarChart3, DollarSign, Users, RefreshCw, TrendingUp, Download, TrendingDown,
} from 'lucide-react';
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, AreaChart, Area,
  ComposedChart,
  XAxis, YAxis, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import { DateRangeBar, DEFAULT_ANALYTICS_PRESETS, subDaysISO, todayISO } from '../../components/DateRangeBar';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const COLORS = ['#FF3B6B', '#06D6A0', '#FFD166', '#118AB2', '#073B4C', '#9B5DE5'];

export const CrmAnalytics = () => {
  const [revenue, setRevenue] = useState(null);
  const [bookings, setBookings] = useState(null);
  const [customers, setCustomers] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activePreset, setActivePreset] = useState('last7');
  const [currentRange, setCurrentRange] = useState({ start: subDaysISO(7), end: todayISO() });
  const [rangeLabel, setRangeLabel] = useState('Last 7 days');
  const [exporting, setExporting] = useState(false);

  const [loyaltyAdj, setLoyaltyAdj] = useState(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (currentRange.start) params.start_date = currentRange.start;
      if (currentRange.end) params.end_date = currentRange.end;
      const [r, b, c, la] = await Promise.all([
        axios.get(`${API_URL}/api/admin/analytics/revenue`, { params, withCredentials: true }),
        axios.get(`${API_URL}/api/admin/analytics/bookings`, { params, withCredentials: true }),
        axios.get(`${API_URL}/api/admin/analytics/customers`, { params, withCredentials: true }),
        axios.get(`${API_URL}/api/admin/analytics/loyalty-adjustments`, { params, withCredentials: true }),
      ]);
      setRevenue(r.data); setBookings(b.data); setCustomers(c.data); setLoyaltyAdj(la.data);
    } catch {
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  }, [currentRange.start, currentRange.end]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const exportPdf = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (currentRange.start) params.set('start_date', currentRange.start);
      if (currentRange.end) params.set('end_date', currentRange.end);
      params.set('label', rangeLabel);
      // Authenticated download via fetch + blob (so cookies travel)
      const res = await fetch(`${API_URL}/api/admin/analytics/report.pdf?${params.toString()}`, {
        credentials: 'include',
      });
      if (!res.ok) throw new Error(`Server returned ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const safe = rangeLabel.replace(/\s+/g, '_').replace(/[/→,]/g, '');
      a.download = `analytics_${safe}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('PDF downloaded');
    } catch (e) {
      toast.error(`Could not export PDF: ${e.message || 'unknown error'}`);
    } finally {
      setExporting(false);
    }
  };

  if (loading && !revenue) {
    return <div className="flex items-center justify-center py-12"><RefreshCw className="w-6 h-6 animate-spin text-[#FF3B6B]" /></div>;
  }

  return (
    <div className="space-y-6 max-w-7xl" data-testid="crm-analytics">
      <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }}>
        <h1 className="text-3xl font-bold text-[#1E1E24]" style={{ fontFamily: 'Fredoka' }}>
          Analytics & Reports
        </h1>
        <p className="text-[#6B6B7A] text-sm mt-1">Filter by date range and export a printable PDF.</p>
      </motion.div>

      {/* ── Date Range Bar (shared component — iter-140) ── */}
      <DateRangeBar
        value={currentRange}
        presetId={activePreset}
        onPresetChange={setActivePreset}
        onChange={({ start, end, label }) => {
          setCurrentRange({ start, end });
          setRangeLabel(label);
        }}
        presets={DEFAULT_ANALYTICS_PRESETS}
        loading={loading}
        testId="analytics-range-bar"
        rightSlot={
          <Button
            onClick={exportPdf}
            disabled={exporting}
            className="rounded-full bg-[#FF3B6B] hover:bg-[#E62E58] text-white text-xs font-semibold"
            data-testid="analytics-export-pdf"
          >
            <Download className="w-3.5 h-3.5 mr-1.5" />
            {exporting ? 'Generating…' : 'Save as PDF'}
          </Button>
        }
      />

      {/* ── KPI cards ── */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4" data-testid="analytics-kpi-grid">
        <KpiCard label="Total Revenue" value={`$${(revenue?.total_revenue || 0).toLocaleString()}`} icon={DollarSign} color="#06D6A0" />
        <KpiCard label="Refunds" value={`-$${(revenue?.total_refunds || 0).toLocaleString()}`} icon={TrendingDown} color="#D4302A" testId="analytics-kpi-refunds" />
        <KpiCard label="Net Revenue" value={`$${(revenue?.net_revenue || 0).toLocaleString()}`} icon={TrendingUp} color="#FF3B6B" />
        <KpiCard label="Customers" value={(customers?.total_customers || 0).toLocaleString()} icon={Users} color="#118AB2" />
        <KpiCard label="Bookings" value={(revenue?.total_paid_bookings || 0).toLocaleString()} icon={BarChart3} color="#FFD166" />
      </div>

      {/* ── Charts grid ── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ChartCard title="Monthly Revenue">
          <ResponsiveContainer width="100%" height={250}>
            <AreaChart data={revenue?.monthly_revenue || []}>
              <defs>
                <linearGradient id="rev" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#06D6A0" stopOpacity={0.4} />
                  <stop offset="100%" stopColor="#06D6A0" stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis dataKey="month" stroke="#6B6B7A" fontSize={11} />
              <YAxis stroke="#6B6B7A" fontSize={11} />
              <Tooltip contentStyle={{ borderRadius: 12, border: '1px solid #E8E5DF' }} />
              <Area type="monotone" dataKey="revenue" stroke="#06D6A0" strokeWidth={2.5} fill="url(#rev)" />
            </AreaChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Revenue by Package">
          <ResponsiveContainer width="100%" height={250}>
            <PieChart>
              <Pie data={revenue?.by_package || []} dataKey="revenue" nameKey="package" cx="50%" cy="50%" outerRadius={80} label>
                {(revenue?.by_package || []).map((entry, i) => (
                  <Cell key={i} fill={COLORS[i % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip contentStyle={{ borderRadius: 12, border: '1px solid #E8E5DF' }} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Bookings by Status">
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={bookings?.by_status || []}>
              <XAxis dataKey="status" stroke="#6B6B7A" fontSize={11} />
              <YAxis stroke="#6B6B7A" fontSize={11} />
              <Tooltip contentStyle={{ borderRadius: 12, border: '1px solid #E8E5DF' }} />
              <Bar dataKey="count" radius={[8, 8, 0, 0]} fill="#FF3B6B" />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Time Slot Popularity">
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={bookings?.by_time_slot || []}>
              <XAxis
                dataKey="time_slot"
                stroke="#6B6B7A"
                fontSize={11}
                tickFormatter={(t) => (t === '10:00' ? '10 AM' : t === '13:00' ? '1 PM' : t === '16:00' ? '4 PM' : t)}
              />
              <YAxis stroke="#6B6B7A" fontSize={11} />
              <Tooltip contentStyle={{ borderRadius: 12, border: '1px solid #E8E5DF' }} />
              <Bar dataKey="count" radius={[8, 8, 0, 0]} fill="#FFD166" />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      <ChartCard title="New Customers by Month">
        <ResponsiveContainer width="100%" height={250}>
          <LineChart data={customers?.new_customers_by_month || []}>
            <XAxis dataKey="month" stroke="#6B6B7A" fontSize={11} />
            <YAxis stroke="#6B6B7A" fontSize={11} />
            <Tooltip contentStyle={{ borderRadius: 12, border: '1px solid #E8E5DF' }} />
            <Line type="monotone" dataKey="new_customers" stroke="#118AB2" strokeWidth={2.5} dot={{ r: 4 }} />
          </LineChart>
        </ResponsiveContainer>
      </ChartCard>

      <LoyaltyAdjustmentsCard data={loyaltyAdj} range={currentRange} />

      <InventoryCogsChart />
    </div>
  );
};

const KpiCard = ({ label, value, icon: Icon, color, testId }) => (
  <div className="bg-white rounded-2xl border border-[#E8E5DF] p-4 flex items-center gap-3" data-testid={testId}>
    <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: `${color}20` }}>
      <Icon className="w-5 h-5" style={{ color }} />
    </div>
    <div>
      <p className="text-[10px] uppercase font-semibold text-[#6B6B7A] tracking-wider">{label}</p>
      <p className="text-xl font-bold text-[#1E1E24]">{value}</p>
    </div>
  </div>
);

const ChartCard = ({ title, children, action }) => (
  <div className="bg-white rounded-2xl border border-[#E8E5DF] p-5">
    <div className="flex items-center justify-between mb-4">
      <h3 className="text-base font-bold text-[#1E1E24]">{title}</h3>
      {action}
    </div>
    {children}
  </div>
);

// ─── Inventory COGS / Profit chart ───────────────────────────────────────
// Pulls /api/admin/inventory/cogs?months=N and renders a combined chart:
//   - Bars: COGS (cost of goods sold) and wastage cost
//   - Line: revenue from selling those goods
// Plus a totals footer with profit and tx_count summary.
const InventoryCogsChart = () => {
  const [months, setMonths] = useState(6);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    axios.get(`${API_URL}/api/admin/inventory/cogs`, { params: { months }, withCredentials: true })
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch(() => { if (!cancelled) setData({ months: [], totals: { cogs: 0, revenue: 0, profit: 0, tx_count: 0 } }); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [months]);

  const monthLabel = (m) => {
    if (!m) return m;
    const [y, mo] = m.split('-');
    return new Date(Number(y), Number(mo) - 1, 1).toLocaleDateString('en-CA', { month: 'short', year: '2-digit' });
  };
  const rows = (data?.months || []).map((r) => ({ ...r, label: monthLabel(r.month) }));
  const fmtMoney = (n) => `$${Number(n || 0).toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const totals = data?.totals || { cogs: 0, revenue: 0, profit: 0, wastage_cost: 0, tx_count: 0 };
  const isProfit = (totals.profit || 0) >= 0;

  return (
    <ChartCard
      title="Inventory COGS & Profit"
      action={
        <select
          value={months}
          onChange={(e) => setMonths(Number(e.target.value))}
          className="h-8 rounded-xl border border-[#E8E5DF] text-xs bg-white px-2"
          data-testid="cogs-month-range"
        >
          <option value="3">Last 3 months</option>
          <option value="6">Last 6 months</option>
          <option value="12">Last 12 months</option>
          <option value="24">Last 24 months</option>
        </select>
      }
    >
      {loading ? (
        <div className="py-12 flex justify-center">
          <RefreshCw className="w-5 h-5 animate-spin text-[#FF3B6B]" />
        </div>
      ) : (
        <>
          <ResponsiveContainer width="100%" height={260}>
            <ComposedChart data={rows} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
              <XAxis dataKey="label" stroke="#6B6B7A" fontSize={11} />
              <YAxis
                stroke="#6B6B7A"
                fontSize={11}
                tickFormatter={(v) => (v >= 1000 ? `${(v / 1000).toFixed(1)}k` : v)}
              />
              <Tooltip
                contentStyle={{ borderRadius: 12, border: '1px solid #E8E5DF' }}
                formatter={(v, key) => [fmtMoney(v), key === 'cogs' ? 'COGS' : key === 'revenue' ? 'Revenue' : key === 'wastage_cost' ? 'Wastage' : 'Profit']}
              />
              <Legend wrapperStyle={{ fontSize: 12, paddingTop: 8 }} />
              <Bar dataKey="cogs"         name="COGS"     fill="#FF7A2E" radius={[6, 6, 0, 0]} />
              <Bar dataKey="wastage_cost" name="Wastage"  fill="#FF3B6B" radius={[6, 6, 0, 0]} />
              <Line type="monotone" dataKey="revenue" name="Revenue" stroke="#06D6A0" strokeWidth={2.5} dot={{ r: 3 }} />
            </ComposedChart>
          </ResponsiveContainer>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-4 pt-4 border-t border-[#F0EDE6]">
            <CogsStat label="Revenue"  value={fmtMoney(totals.revenue)}  color="#06D6A0" testId="cogs-total-revenue" />
            <CogsStat label="COGS"     value={fmtMoney(totals.cogs)}     color="#FF7A2E" testId="cogs-total-cogs" />
            <CogsStat
              label="Profit"
              value={fmtMoney(totals.profit)}
              color={isProfit ? '#06D6A0' : '#FF3B6B'}
              sub={totals.revenue > 0 ? `${Math.round((totals.profit / totals.revenue) * 100)}% margin` : ''}
              testId="cogs-total-profit"
            />
            <CogsStat label="Wastage cost" value={fmtMoney(totals.wastage_cost)} color="#FF3B6B" testId="cogs-total-wastage" />
          </div>
          {(rows.length > 0 && rows.every((r) => r.cogs === 0 && r.revenue === 0)) && (
            <p className="text-center text-xs text-[#9a9aa6] mt-3">
              No inventory consumption recorded in this window — apply pending deductions or record usage to populate.
            </p>
          )}
        </>
      )}
    </ChartCard>
  );
};

const CogsStat = ({ label, value, color, sub, testId }) => (
  <div className="bg-white rounded-xl border border-[#F0EDE6] p-3" data-testid={testId}>
    <p className="text-[10px] uppercase font-semibold text-[#6B6B7A] tracking-wider">{label}</p>
    <p className="text-base font-bold" style={{ color }}>{value}</p>
    {sub && <p className="text-[10px] text-[#9a9aa6] mt-0.5">{sub}</p>}
  </div>
);


// ─── Loyalty Adjustments Report (iter-101) ───────────────────────
const LoyaltyAdjustmentsCard = ({ data, range }) => {
  const [drillEmp, setDrillEmp] = useState(null);   // {employee_id, employee_name} | null
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);

  const openDrill = async (emp) => {
    setDrillEmp(emp);
    setLoading(true);
    setEntries([]);
    try {
      const params = { employee_id: emp.employee_id, limit: 100 };
      if (range?.start) params.start_date = range.start;
      if (range?.end) params.end_date = range.end;
      const r = await axios.get(`${API_URL}/api/admin/analytics/loyalty-adjustments/entries`, {
        params, withCredentials: true,
      });
      setEntries(r.data || []);
    } catch {
      toast.error('Could not load adjustment history');
    } finally {
      setLoading(false);
    }
  };

  const totals = data?.totals || { count: 0, credits: 0, debits: 0, net: 0 };
  const byEmployee = data?.by_employee || [];
  const byDay = data?.by_day || [];
  const topReasons = data?.top_reasons || [];

  return (
    <ChartCard title="Loyalty Adjustments — staff oversight">
      <div className="space-y-4" data-testid="loyalty-adj-card">
        {/* Totals row */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <CogsStat label="Adjustments" value={totals.count.toLocaleString()} color="#118AB2" testId="loyadj-total-count" />
          <CogsStat label="Credits issued" value={`+${totals.credits.toLocaleString()} pts`} color="#06D6A0" testId="loyadj-total-credits" />
          <CogsStat label="Debits issued" value={`-${totals.debits.toLocaleString()} pts`} color="#FF3B6B" testId="loyadj-total-debits" />
          <CogsStat
            label="Net"
            value={`${totals.net >= 0 ? '+' : ''}${totals.net.toLocaleString()} pts`}
            color={totals.net >= 0 ? '#06D6A0' : '#FF3B6B'}
            testId="loyadj-total-net"
          />
        </div>

        {/* Sparkline */}
        {byDay.length > 0 && (
          <div className="h-32" data-testid="loyadj-sparkline">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={byDay}>
                <XAxis dataKey="date" stroke="#6B6B7A" fontSize={10} tick={false} axisLine={false} />
                <YAxis stroke="#6B6B7A" fontSize={10} hide />
                <Tooltip
                  contentStyle={{ borderRadius: 12, border: '1px solid #E8E5DF', fontSize: 12 }}
                  formatter={(v, key) => key === 'net' ? [`${v >= 0 ? '+' : ''}${v} pts`, 'Net'] : [v, 'Count']}
                />
                <Bar dataKey="net" radius={[6, 6, 0, 0]}>
                  {byDay.map((d, i) => (
                    <Cell key={i} fill={d.net >= 0 ? '#06D6A0' : '#FF3B6B'} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Per-employee + top reasons */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Per-employee */}
          <div className="bg-white border border-[#F0EDE6] rounded-xl overflow-hidden" data-testid="loyadj-by-employee">
            <div className="px-3 py-2 border-b border-[#F0EDE6] flex items-center justify-between">
              <p className="text-xs uppercase font-semibold text-[#6B6B7A] tracking-wider">By employee</p>
              <span className="text-[10px] text-[#9a9aa6]">click to drill down</span>
            </div>
            {byEmployee.length === 0 ? (
              <p className="px-3 py-6 text-xs text-[#9a9aa6] text-center" data-testid="loyadj-by-employee-empty">
                No adjustments in this range.
              </p>
            ) : (
              <ul className="divide-y divide-[#F0EDE6] max-h-64 overflow-y-auto">
                {byEmployee.map((e) => (
                  <li key={e.employee_id}>
                    <button
                      type="button"
                      onClick={() => openDrill(e)}
                      className="w-full text-left px-3 py-2 hover:bg-[#FFFDF8] flex items-center justify-between gap-3 transition"
                      data-testid={`loyadj-emp-${e.employee_id}`}
                    >
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-[#1E1E24] truncate">{e.employee_name}</p>
                        <p className="text-[10px] text-[#6B6B7A]">{e.count} adjustment{e.count === 1 ? '' : 's'} · last {e.last_at ? new Date(e.last_at).toLocaleDateString() : '—'}</p>
                      </div>
                      <div className="text-right shrink-0">
                        <p className="text-[10px] text-[#06D6A0]">+{e.credits.toLocaleString()}</p>
                        <p className="text-[10px] text-[#FF3B6B]">-{e.debits.toLocaleString()}</p>
                        <p className={`text-xs font-bold ${e.net >= 0 ? 'text-[#06D6A0]' : 'text-[#FF3B6B]'}`}>
                          net {e.net >= 0 ? '+' : ''}{e.net.toLocaleString()}
                        </p>
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Top reasons */}
          <div className="bg-white border border-[#F0EDE6] rounded-xl overflow-hidden" data-testid="loyadj-top-reasons">
            <div className="px-3 py-2 border-b border-[#F0EDE6]">
              <p className="text-xs uppercase font-semibold text-[#6B6B7A] tracking-wider">Top reasons (truncated)</p>
            </div>
            {topReasons.length === 0 ? (
              <p className="px-3 py-6 text-xs text-[#9a9aa6] text-center">No reasons recorded.</p>
            ) : (
              <ul className="divide-y divide-[#F0EDE6] max-h-64 overflow-y-auto">
                {topReasons.map((r, i) => (
                  <li key={i} className="px-3 py-2 flex items-center justify-between gap-3">
                    <p className="text-xs text-[#1E1E24] truncate flex-1" title={r.reason}>{r.reason}</p>
                    <span className="text-[10px] text-[#6B6B7A] shrink-0">{r.count}× · net {r.net >= 0 ? '+' : ''}{r.net}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>

      {/* Drill-down dialog */}
      {drillEmp && (
        <div
          className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setDrillEmp(null)}
          data-testid="loyadj-drilldown"
        >
          <div
            className="bg-white rounded-2xl max-w-2xl w-full max-h-[80vh] flex flex-col overflow-hidden shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-5 py-4 border-b border-[#E8E5DF] flex items-center justify-between">
              <div>
                <p className="text-xs uppercase tracking-widest text-[#6B6B7A]">Adjustment history</p>
                <p className="font-semibold text-[#1E1E24]">{drillEmp.employee_name}</p>
              </div>
              <button
                type="button"
                onClick={() => setDrillEmp(null)}
                className="text-[#6B6B7A] hover:text-[#1E1E24]"
                data-testid="loyadj-close-drill"
              >
                ✕
              </button>
            </div>
            <div className="flex-1 overflow-y-auto">
              {loading ? (
                <p className="px-5 py-10 text-center text-sm text-[#6B6B7A]">Loading…</p>
              ) : entries.length === 0 ? (
                <p className="px-5 py-10 text-center text-sm text-[#6B6B7A]">No entries in this range.</p>
              ) : (
                <table className="w-full text-sm">
                  <thead className="bg-[#FFFDF8] sticky top-0">
                    <tr className="text-left text-[10px] uppercase tracking-widest text-[#6B6B7A] border-b border-[#E8E5DF]">
                      <th className="px-3 py-2">When</th>
                      <th className="px-3 py-2">Customer</th>
                      <th className="px-3 py-2 text-right">Pts</th>
                      <th className="px-3 py-2">Reason</th>
                    </tr>
                  </thead>
                  <tbody>
                    {entries.map((e) => (
                      <tr key={e.ledger_id} className="border-b border-[#F0EDE6] last:border-b-0" data-testid={`loyadj-entry-${e.ledger_id}`}>
                        <td className="px-3 py-2 text-xs text-[#6B6B7A] whitespace-nowrap">
                          {e.created_at ? new Date(e.created_at).toLocaleString('en-CA', { dateStyle: 'short', timeStyle: 'short' }) : '—'}
                        </td>
                        <td className="px-3 py-2 text-xs text-[#1E1E24] truncate max-w-[180px]">{e.user_email}</td>
                        <td className={`px-3 py-2 text-xs font-bold text-right ${e.points >= 0 ? 'text-[#06D6A0]' : 'text-[#FF3B6B]'}`}>
                          {e.points > 0 ? '+' : ''}{e.points}
                        </td>
                        <td className="px-3 py-2 text-xs text-[#1E1E24]" title={e.description}>{e.description || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}
    </ChartCard>
  );
};

export default CrmAnalytics;
